<template>
  <div :class="classes">
    <AppContainer>
      <AppButton v-if="onGoBack" class="mb-6" variant="text" @click="onGoBack">
        <template #icon-start-circle><IconChevronLeft /></template>
        {{ $t('string.Back') }}
      </AppButton>

      <template v-if="title">
        <h3 class="text-primary-nav">{{ title }}</h3>

        <AppDivider class="my-5" />
      </template>

      <template v-for="(group, groupIndex) in menu" :key="groupIndex">
        <ul class="app-menu-mobile-menu__group">
          <li v-for="(item, itemIndex) in group" :key="itemIndex">
            <a
              v-if="item.children || item.action"
              :class="depth > 0 ? 'submenu-link' : 'text-primary-nav'"
              role="button"
              tabindex="0"
              :aria-pressed="item.selected"
              @keydown.enter.prevent="
                ($event.currentTarget as HTMLElement).click()
              "
              @keydown.space.prevent="
                ($event.currentTarget as HTMLElement).click()
              "
              @click.prevent="
                item.action
                  ? item.action()
                  : openSubmenu(`${depth}-${groupIndex}-${itemIndex}`)
              "
            >
              <component :is="item.icon" v-if="item.icon" class="icon" />

              <img
                v-if="item.flag"
                class="country-flag"
                :src="item.flag.src"
                :alt="item.flag.alt"
              />

              <AppAvatar
                v-if="item.initials"
                :initials="item.initials"
                class="mr-3"
              />

              {{ item.title }}
              <span v-if="item.selected" aria-hidden="true" class="ml-2"
                >✓</span
              >

              <IconChevronDown v-if="item.children" class="icon-arrow" />
            </a>

            <NuxtLink
              v-else
              :to="item.url"
              :external="item.external"
              :target="item.external ? '_blank' : undefined"
              :rel="item.external ? 'noopener noreferrer' : undefined"
              :class="depth > 0 ? 'submenu-link' : 'text-primary-nav'"
              @click="menus.closeMobileMenu"
            >
              <component :is="item.icon" v-if="item.icon" class="icon" />

              <img
                v-if="item.flag"
                class="country-flag"
                :src="item.flag.src"
                :alt="item.flag.alt"
              />

              <AppAvatar
                v-if="item.initials"
                :initials="item.initials"
                class="mr-3"
              />

              {{ item.title }}
            </NuxtLink>

            <template v-if="item.children">
              <Transition name="mobile-submenu">
                <AppMenuMobileMenu
                  v-show="
                    activeSubmenu === `${depth}-${groupIndex}-${itemIndex}`
                  "
                  :menu="item.children"
                  :title="item.title"
                  :all-link="
                    item.url
                      ? {
                          title: item.title,
                          url: item.url,
                          external: item.external,
                        }
                      : undefined
                  "
                  :depth="depth + 1"
                  :on-go-back="goBack"
                />
              </Transition>
            </template>
          </li>
        </ul>

        <AppDivider v-if="groupIndex < menu.length - 1" class="my-5" />
      </template>

      <div class="mt-6">
        <NuxtLink
          v-if="allLink"
          :to="allLink.url"
          :external="allLink.external"
          :target="allLink.external ? '_blank' : undefined"
          :rel="allLink.external ? 'noopener noreferrer' : undefined"
          class="text-red hover:text-tint-red-hover text-xs"
          @click="menus.closeMobileMenu"
        >
          {{ $t('string.Show-all') }} {{ allLink.title }}
        </NuxtLink>
      </div>
    </AppContainer>
  </div>
</template>

<script setup lang="ts">
import type { MenuData } from '~/queries'

type Props = {
  menu: MenuData[number] & { children: MenuData[number][][] }
  title?: string
  allLink?: {
    title: string
    url: string
    external: boolean
  }
  depth?: number
  onGoBack?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  allLink: undefined,
  depth: 0,
  onGoBack: undefined,
})

const menus = useMenusStore()

const classes = computed(() => {
  return {
    'app-menu-mobile-menu': true,
    'app-menu-mobile-menu--submenu': props.depth > 0,
    'app-menu-mobile-menu--has-submenu': activeSubmenu.value !== null,
  }
})

const activeSubmenu = ref<string | null>(null)

const openSubmenu = (id: string) => (activeSubmenu.value = id)
const goBack = () => (activeSubmenu.value = null)
</script>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-menu-mobile-menu {
  position: relative;
  height: 100%;
  padding: 1.5rem 0;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  box-shadow: 0 0 2.5rem 0 rgba($black, 0.1);
  z-index: 1;

  &--submenu {
    position: fixed;
    top: var(--navbar-offset-height, 3.5rem);
    bottom: 0;
    left: 0;
    width: 100%;
    height: auto;

    background-color: $white;
  }

  &--has-submenu {
    overflow: hidden;
  }
}

.app-menu-mobile-menu__group {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    margin-bottom: 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    min-height: 2.5rem;
    text-decoration: none;
    cursor: pointer;
    @include transition('color');

    &:hover {
      color: $tint-red-hover;
    }

    .icon {
      flex: 0 0 auto;
      width: 1.25rem;
      height: 1.25rem;
      margin-right: 0.75rem;
    }

    .icon-arrow {
      flex: 0 0 auto;
      width: 1.25rem;
      height: 1.25rem;
      margin-left: auto;
      transform: rotate(-90deg);
    }

    .country-flag {
      flex: 0 0 auto;
      width: 1.5rem;
      height: 1.5rem;
      margin-right: 0.75rem;
      border-radius: 50%;
      object-fit: cover;
    }
  }

  .submenu-link {
    font-family: $font-barlow;
    font-size: 1.375rem;
    line-height: 1.1;
    letter-spacing: 0.02272727em;
  }
}
</style>
